import { Alert, Box, Center, Loader, Stack, Text, Title } from '@mantine/core';
import { getDisplayString } from '@medplum/core';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getZoomMeetingFromAppointment } from '@/lib/zoom/appointmentZoomMeeting';
import { formatUnknownError } from '@/lib/zoom/formatUnknownError';
import {
  focusOpenerFromHostTab,
  openInPersonRecordingChannel,
  type InPersonRecordingBridgeMessage,
  type InPersonRecordingBridgeMessageBody,
} from '@/lib/zoom/inPersonRecordingBridge';
import {
  hideClientViewRoot,
  joinZoomMeeting,
  leaveZoomMeeting,
  listZoomAudioInputDevices,
  preferZoomAudioInputDevice,
  setZoomMicMuted,
  type ZoomClient,
} from '@/lib/zoom/zoomMeetingSdk';
import { isZoomEnabled } from '@/lib/zoom/useZoomMeeting';
import {
  clearZoomCoiRetryMarker,
  decideZoomCoiAction,
  readZoomCoiRetryMarker,
  writeZoomCoiRetryMarker,
} from '@/pages/session/lib/zoomCoiIsolation';

type IsolationStatus = 'pending' | 'ready' | 'failed';

let coiReloadRequestedInThisDocument = false;

function tearDownZoomDom(): void {
  hideClientViewRoot();
  const root = document.getElementById('zmmtg-root');
  if (root) {
    root.replaceChildren();
    root.style.display = 'none';
  }
  // Zoom sometimes leaves full-viewport overlays outside zmmtg-root.
  document.querySelectorAll('.ReactModal__Overlay, .zoom-portal, #zoom-sdk-style-font').forEach((el) => {
    if (el instanceof HTMLElement) {
      el.style.display = 'none';
    }
  });
}

/**
 * Minimal COI host popup for in-person Zoom cloud recording.
 * Controls (timer / mute / end) live on the appointment page via BroadcastChannel.
 */
export function InPersonRecordingPage(): JSX.Element {
  const [isolationStatus, setIsolationStatus] = useState<IsolationStatus>(() =>
    window.crossOriginIsolated ? 'ready' : 'pending',
  );

  useEffect(() => {
    if (isolationStatus === 'ready') {
      clearZoomCoiRetryMarker(sessionStorage);
      return;
    }
    if (isolationStatus !== 'pending' || coiReloadRequestedInThisDocument) {
      return;
    }
    const action = decideZoomCoiAction({
      crossOriginIsolated: window.crossOriginIsolated,
      alreadyRetried: readZoomCoiRetryMarker(sessionStorage),
    });
    if (action.kind === 'proceed') {
      setIsolationStatus('ready');
      return;
    }
    if (action.kind === 'reload' && writeZoomCoiRetryMarker(sessionStorage)) {
      coiReloadRequestedInThisDocument = true;
      window.location.reload();
      return;
    }
    setIsolationStatus('failed');
  }, [isolationStatus]);

  if (isolationStatus === 'pending') {
    return <></>;
  }

  if (isolationStatus === 'failed') {
    return (
      <Text c="red" ta="center" mt="xl" maw={480} mx="auto" px="md">
        This browser session could not enable the features required for in-person recording.
        Close this window and try again from the appointment page, or use Chrome or Edge.
      </Text>
    );
  }

  return <InPersonRecordingHostContent />;
}

function InPersonRecordingHostContent(): JSX.Element {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const zoomClientRef = useRef<ZoomClient | null>(null);
  const joinStartedRef = useRef(false);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const isMutedRef = useRef(false);
  const sessionIdRef = useRef(
    new URLSearchParams(window.location.search).get('sessionId')?.trim() ?? '',
  );
  const selectedAudioDeviceIdRef = useRef<string | null>(null);

  const [status, setStatus] = useState<'joining' | 'joined' | 'error'>('joining');
  const [joinError, setJoinError] = useState<string | null>(null);

  const providerName = (profile ? getDisplayString(profile) : '') || 'Provider';
  const providerEmail = profile?.telecom?.find((t) => t.system === 'email')?.value;

  const post = useCallback((message: InPersonRecordingBridgeMessageBody) => {
    const sessionId = sessionIdRef.current;
    if (!sessionId) return;
    channelRef.current?.postMessage({ ...message, sessionId } as InPersonRecordingBridgeMessage);
  }, []);

  const publishAudioDevices = useCallback(async () => {
    try {
      const devices = await listZoomAudioInputDevices();
      if (!selectedAudioDeviceIdRef.current && devices[0]) {
        selectedAudioDeviceIdRef.current = devices[0].deviceId;
      }
      post({
        type: 'audio-devices',
        devices,
        selectedDeviceId: selectedAudioDeviceIdRef.current,
      });
    } catch (e) {
      console.error('[InPersonRecordingHost] list audio devices failed:', e);
    }
  }, [post]);

  const leaveOnly = useCallback(async () => {
    if (zoomClientRef.current) {
      await leaveZoomMeeting(zoomClientRef.current);
      zoomClientRef.current = null;
    }
    tearDownZoomDom();
  }, []);

  /** Appointment page owns FHIR end — leave Zoom and close this script-opened popup. */
  const finishHostSession = useCallback(async () => {
    await leaveOnly();
    post({ type: 'ended' });
    try {
      window.close();
    } catch {
      // ignore
    }
  }, [leaveOnly, post]);

  // Best-effort yield focus to the appointment opener after load / COI reload.
  useEffect(() => {
    focusOpenerFromHostTab();
    const delays = [0, 300, 1000, 2000];
    const timers = delays.map((ms) => window.setTimeout(() => focusOpenerFromHostTab(), ms));
    return () => {
      for (const id of timers) window.clearTimeout(id);
    };
  }, []);

  useEffect(() => {
    if (!appointmentId) return undefined;
    const channel = openInPersonRecordingChannel(appointmentId);
    channelRef.current = channel;
    post({ type: 'host-hello' });
    if (channel) {
      channel.onmessage = (event: MessageEvent<InPersonRecordingBridgeMessage>) => {
        const msg = event.data;
        if (!msg || typeof msg !== 'object' || !('type' in msg)) return;
        if (!('sessionId' in msg) || msg.sessionId !== sessionIdRef.current) return;
        if (msg.type === 'set-mute') {
          const client = zoomClientRef.current;
          if (!client) return;
          void setZoomMicMuted(client, msg.muted)
            .then(() => {
              isMutedRef.current = msg.muted;
              post({ type: 'mute-state', muted: msg.muted });
            })
            .catch((e) => console.error('[InPersonRecordingHost] mute failed:', e));
        } else if (msg.type === 'set-audio-input') {
          selectedAudioDeviceIdRef.current = msg.deviceId;
          void preferZoomAudioInputDevice(msg.deviceId)
            .then(() => publishAudioDevices())
            .catch((e) => console.error('[InPersonRecordingHost] set audio input failed:', e));
        } else if (msg.type === 'request-devices') {
          void publishAudioDevices();
        } else if (msg.type === 'end') {
          void finishHostSession();
        }
      };
    }
    return () => {
      channel?.close();
      channelRef.current = null;
    };
  }, [appointmentId, finishHostSession, post, publishAudioDevices]);

  useEffect(() => {
    if (!appointmentId || joinStartedRef.current || !isZoomEnabled()) return;
    joinStartedRef.current = true;

    const isMeetingNotFound = (err: unknown): boolean => {
      const text = formatUnknownError(err).toLowerCase();
      return text.includes('not found') || text.includes('3707');
    };

    const sleep = (ms: number) => new Promise((r) => window.setTimeout(r, ms));

    void (async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const queryMn = urlParams.get('zoomMeetingNumber')?.trim() ?? '';
        const queryPw = urlParams.get('zoomPassword') ?? '';
        const sessionId = urlParams.get('sessionId')?.trim() ?? '';
        if (sessionId) sessionIdRef.current = sessionId;
        let meetingNumber = queryMn;
        let password = queryPw;
        if (!meetingNumber) {
          const appt = await medplum.readResource('Appointment', appointmentId, {
            cache: 'no-cache',
          });
          const fromAppt = getZoomMeetingFromAppointment(appt);
          meetingNumber = fromAppt.meetingNumber;
          password = fromAppt.password;
        }
        if (!meetingNumber) {
          throw new Error(
            'No Zoom meeting found for this appointment. Start recording from the appointment page.',
          );
        }
        if (!sessionIdRef.current) {
          throw new Error('Missing recording session id. Start recording from the appointment page.');
        }

        let lastError: unknown;
        let client: ZoomClient | null = null;
        for (let attempt = 0; attempt < 4; attempt++) {
          try {
            if (attempt > 0) {
              tearDownZoomDom();
              await sleep(1500 * attempt);
            }
            client = await joinZoomMeeting(medplum, {
              meetingNumber,
              password,
              userName: providerName,
              userEmail: providerEmail,
              role: 1,
              leaveUrl: `${window.location.origin}/dashboard/appointments/${appointmentId}`,
              hiddenUi: true,
            });
            lastError = undefined;
            break;
          } catch (err) {
            lastError = err;
            tearDownZoomDom();
            if (!isMeetingNotFound(err) || attempt === 3) throw err;
          }
        }
        if (!client) throw lastError ?? new Error('Could not join Zoom meeting');

        zoomClientRef.current = client;
        const joinedAt = Date.now();
        setStatus('joined');
        post({ type: 'joined', joinedAt });
        focusOpenerFromHostTab();
        void publishAudioDevices();
      } catch (err) {
        await leaveOnly();
        tearDownZoomDom();
        const message = formatUnknownError(err);
        setJoinError(message);
        setStatus('error');
        post({
          type: 'join-error',
          message,
        });
      }
    })();
  }, [appointmentId, leaveOnly, medplum, post, providerEmail, providerName, publishAudioDevices]);

  useEffect(() => {
    if (status !== 'joined') return undefined;
    const onDeviceChange = () => {
      void publishAudioDevices();
    };
    navigator.mediaDevices?.addEventListener?.('devicechange', onDeviceChange);
    return () => {
      navigator.mediaDevices?.removeEventListener?.('devicechange', onDeviceChange);
    };
  }, [publishAudioDevices, status]);

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (!zoomClientRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    const onPageHide = () => {
      // Never call the end bot here: COI bootstrap reloads this document.
      // Appointment page owns FHIR end (started-at / ended-at).
      post({ type: 'host-closing' });
      if (zoomClientRef.current) {
        void leaveZoomMeeting(zoomClientRef.current).catch(() => undefined);
      }
    };
    window.addEventListener('beforeunload', warn);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('beforeunload', warn);
      window.removeEventListener('pagehide', onPageHide);
      if (zoomClientRef.current) {
        void leaveZoomMeeting(zoomClientRef.current);
        hideClientViewRoot();
      }
    };
  }, [post]);

  if (!isZoomEnabled()) {
    return (
      <Center h="100vh">
        <Text c="dimmed">Zoom is not enabled.</Text>
      </Center>
    );
  }

  if (status === 'joining') {
    return (
      <Center h="100vh" px="md" bg="gray.0">
        <Stack align="center" gap="lg" maw={440}>
          <Loader size="lg" color="gold" />
          <Title order={3} ta="center">
            Connecting recording…
          </Title>
          <Alert color="yellow" variant="light" w="100%" title="Keep this window open">
            Closing this window may stop the Zoom cloud recording. Switch back to your appointment
            tab to document and use Recording controls.
          </Alert>
        </Stack>
      </Center>
    );
  }

  if (status === 'error') {
    return (
      <Center h="100vh" px="md" style={{ position: 'relative', zIndex: 100000 }}>
        <Stack align="center" gap="md" maw={480}>
          <Text c="red" ta="center">
            {joinError ?? 'Could not join the recording meeting.'}
          </Text>
          <Text size="sm" c="dimmed" ta="center">
            Switch back to your appointment tab to retry. You can close this window.
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Center h="100vh" px="md" bg="gray.0">
      <Box maw={440}>
        <Stack align="center" gap="lg">
          <Title order={2} ta="center">
            Recording in progress
          </Title>
          <Alert color="yellow" variant="light" w="100%" title="Keep this window open">
            Closing this window may stop the Zoom cloud recording. Switch back to your appointment
            tab for notes, mute, microphone, and End meeting.
          </Alert>
          <Text size="sm" c="dimmed" ta="center">
            Leave this window running in the background — do not close it until the session ends.
          </Text>
        </Stack>
      </Box>
    </Center>
  );
}

export function isInPersonRecordingRoute(pathname: string): boolean {
  return /^\/dashboard\/appointments\/[^/]+\/in-person-recording\/?$/.test(pathname);
}
